import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SourceBadge, Stat } from "./atoms";
import { type CostsResponse, fmtBytes, fmtUsd } from "./types";

type Props = {
  data: CostsResponse;
  environment: "sandbox" | "live";
};

export function CostBreakdownCards({ data, environment }: Props) {
  const egress = data.egress ?? { estimated_usd: 0, monthly_baseline_usd: 0, source: "estimate" };
  const invocations =
    data.invocations ?? { estimated_usd: 0, monthly_baseline_usd: 0, source: "estimate" };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Summary</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4 md:grid-cols-7">
          <Stat label="AI inference" value={fmtUsd(data.ai.total_usd)} sub={`${data.ai.events} events`} />
          <Stat
            label="Storage (est.)"
            value={fmtUsd(data.storage.estimated_usd)}
            sub={fmtBytes(data.storage.total_bytes)}
          />
          <Stat
            label="DB share (est.)"
            value={fmtUsd(data.database.estimated_usd)}
            sub={`${(data.database.share * 100).toFixed(2)}%`}
          />
          <Stat
            label="Egress (est.)"
            value={fmtUsd(egress.estimated_usd)}
            sub={`${fmtUsd(egress.monthly_baseline_usd)}/mo base`}
          />
          <Stat
            label="Invocations (est.)"
            value={fmtUsd(invocations.estimated_usd)}
            sub={`${fmtUsd(invocations.monthly_baseline_usd)}/mo base`}
          />
          <Stat label="Total cost" value={fmtUsd(data.total_cost_usd)} sub={data.window} />
          <Stat
            label="Net (revenue − cost)"
            value={fmtUsd(data.net_contribution_usd)}
            sub={`Rev ${fmtUsd(data.revenue.total_usd)} · ${environment}`}
            tone={data.net_contribution_usd >= 0 ? "good" : "bad"}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>AI inference</CardTitle>
          <SourceBadge source={data.ai.source} />
        </CardHeader>
        <CardContent>
          {data.ai.breakdown.length === 0 ? (
            <p className="text-sm text-muted-foreground">No AI events in this window.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground">
                  <tr>
                    <th className="py-1">Provider</th>
                    <th>Model</th>
                    <th>Job</th>
                    <th className="text-right">Events</th>
                    <th className="text-right">Tokens in</th>
                    <th className="text-right">Tokens out</th>
                    <th className="text-right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {data.ai.breakdown.map((r, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="py-1">{r.provider}</td>
                      <td>{r.model ?? "—"}</td>
                      <td>{r.job_kind}</td>
                      <td className="text-right">{r.events}</td>
                      <td className="text-right">{r.tokens_in.toLocaleString()}</td>
                      <td className="text-right">{r.tokens_out.toLocaleString()}</td>
                      <td className="text-right">{fmtUsd(r.cost_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data.ai.failovers > 0 && (
            <p className="mt-2 text-xs text-muted-foreground">{data.ai.failovers} failover events.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Storage</CardTitle>
          <SourceBadge source={data.storage.source} />
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1">Bucket</th>
                  <th className="text-right">Objects</th>
                  <th className="text-right">Size</th>
                  <th className="text-right">Est. cost</th>
                </tr>
              </thead>
              <tbody>
                {data.storage.by_bucket.map((b) => (
                  <tr key={b.bucket} className="border-t border-border">
                    <td className="py-1">{b.bucket}</td>
                    <td className="text-right">{b.objects}</td>
                    <td className="text-right">{fmtBytes(b.bytes)}</td>
                    <td className="text-right">{fmtUsd(b.estimated_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Rate: {fmtUsd(data.baseline.storage_per_gb_month_usd)} / GB-month · window factor{" "}
            {data.baseline.window_month_fraction.toFixed(3)}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Database footprint</CardTitle>
          <SourceBadge source={data.database.source} />
        </CardHeader>
        <CardContent>
          <p className="mb-2 text-sm text-muted-foreground">
            Workspace owns {data.database.workspace_rows.toLocaleString()} of{" "}
            {data.database.total_rows.toLocaleString()} tracked rows (
            {(data.database.share * 100).toFixed(2)}%). Prorated against a{" "}
            {fmtUsd(data.baseline.db_monthly_usd)}/month baseline.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1">Table</th>
                  <th className="text-right">Workspace</th>
                  <th className="text-right">Total</th>
                  <th className="text-right">Share</th>
                </tr>
              </thead>
              <tbody>
                {data.database.row_counts.map((r) => (
                  <tr key={r.table} className="border-t border-border">
                    <td className="py-1">{r.table}</td>
                    <td className="text-right">{r.workspace_rows.toLocaleString()}</td>
                    <td className="text-right">{r.total_rows.toLocaleString()}</td>
                    <td className="text-right">
                      {r.total_rows > 0
                        ? `${((r.workspace_rows / r.total_rows) * 100).toFixed(1)}%`
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Revenue (Stripe)</CardTitle>
          <SourceBadge source={data.revenue.source} />
        </CardHeader>
        <CardContent>
          {data.revenue.stripe_unavailable_reason && (
            <p className="text-sm text-destructive">
              Stripe unavailable: {data.revenue.stripe_unavailable_reason}
            </p>
          )}
          {!data.revenue.stripe_unavailable_reason && data.revenue.invoices.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No paid invoices for the workspace owner in this window.
            </p>
          )}
          {data.revenue.invoices.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground">
                  <tr>
                    <th className="py-1">Invoice</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th className="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.revenue.invoices.map((inv) => (
                    <tr key={inv.id} className="border-t border-border">
                      <td className="py-1 font-mono text-xs">{inv.id}</td>
                      <td>{inv.created ? new Date(inv.created).toLocaleDateString() : "—"}</td>
                      <td>{inv.status}</td>
                      <td className="text-right">
                        {fmtUsd(inv.amount_paid_usd)} {inv.currency.toUpperCase()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
